import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Bars default to blue; an area defaults to the theme's primary colour. */
  color?: string;
  /** `bars` (one per value) or a filled `area` line, for a dense series (24h of buckets). */
  variant?: "bars" | "area";
  /** The max/min labels to the right of the chart. */
  showRange?: boolean;
  /** The value drawn at full height (a budget) when the series stays below it;
   *  by default the series' own max. */
  ceiling?: number;
}

function areaPath(
  values: number[],
  width: number,
  height: number,
  max: number,
): { line: string; fill: string } {
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  // Keep the stroke inside the box at the top.
  const y = (v: number) => (max > 0 ? height - 1 - (v / max) * (height - 2) : height - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(2)},${y(v).toFixed(2)}`);
  const line = `M${points.join("L")}`;
  return { line, fill: `${line}L${((values.length - 1) * step).toFixed(2)},${height}L0,${height}Z` };
}

function Sparkline({
  values,
  width = 80,
  height = 20,
  color,
  variant = "bars",
  showRange = true,
  ceiling = 0,
}: SparklineProps) {
  if (!values.length) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const top = Math.max(max, ceiling);
  const n = values.length;
  const barW = width / n;
  const gap = Math.max(0.5, barW * 0.15);
  const innerW = barW - gap;
  let chart;
  if (variant === "area") {
    const stroke = color ?? "var(--mui-palette-primary-main)";
    const { line, fill } = areaPath(values, width, height, top);
    chart = (
      <>
        <path d={fill} style={{ fill: stroke, fillOpacity: 0.2 }} />
        <path d={line} style={{ fill: "none", stroke, strokeWidth: 1.5 }} />
      </>
    );
  } else {
    chart = values.map((v, i) => {
      const h = top > 0 ? (v / top) * height : 0;
      return (
        <rect
          key={i}
          x={i * barW}
          y={height - h}
          width={innerW}
          height={h}
          fill={v > 0 ? (color ?? "var(--mui-palette-primary-main)") : "var(--mui-palette-divider)"}
        />
      );
    });
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <svg width={width} height={height} role="img" aria-label={`sparkline max ${max} min ${min}`}>
        {chart}
      </svg>
      {showRange && (
        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
          <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
            {max}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
            {min}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default Sparkline;
